import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment, Tab, Tabs, TextField, Typography } from '@mui/material';
import { Icon } from '@iconify/react';
import DataTable, { Column, RowAction } from './DataTable';
import TransactionDetailsDrawer from './TransactionDetailsDrawer';
import CancelTransactionDialog from './CancelTransactionDialog';
import RTGSActionDialog from './RTGSActionDialog';
import { useToast } from './ui/ToastProvider';
import { DEMO_TRANSACTIONS } from '../utils/demoTransactions';
import { MOCK_FAILED_TRANSACTIONS } from './FailedTransactionsTable';
import { transactionStore } from '../utils/transactionStore';
type FailedActionTabId = 'all' | 'rtgs' | 'cancel' | 'coc-cancel' | 'retry' | 'manual-settlement';
interface FailedActionTab {
  id: FailedActionTabId;
  label: string;
  icon: string;
}
const ACTION_TABS: FailedActionTab[] = [{
  id: 'all',
  label: 'All',
  icon: 'lucide:list'
}, {
  id: 'rtgs',
  label: 'Eligible for Move to RTGS',
  icon: 'lucide:arrow-right-circle'
}, {
  id: 'cancel',
  label: 'Eligible for Cancel',
  icon: 'lucide:x'
}, {
  id: 'coc-cancel',
  label: 'COC Eligible to Cancel',
  icon: 'lucide:landmark'
}, {
  id: 'retry',
  label: 'Retry',
  icon: 'lucide:refresh-cw'
}, {
  id: 'manual-settlement',
  label: 'Eligible for Manual Settlement',
  icon: 'lucide:stamp'
}];
const REASON_LABELS: Record<string, string> = {
  DUPLICATE_TXN: 'Duplicate Transaction',
  INVALID_ACCOUNT: 'Invalid Account',
  TITLE_MISMATCH: 'Title Mismatch',
  SYSTEM_TIMEOUT: 'System Timeout',
  BENEFICIARY_BANK_REJECTED: 'Beneficiary Bank Rejected',
  ACCOUNT_CLOSED: 'Account Closed',
  SCREENING_TIMEOUT: 'Screening Timeout',
  INSUFFICIENT_BALANCE: 'Insufficient Balance',
  BALANCE_CHECK_TIMEOUT: 'Balance Inquiry Failed With Code: 500, Desc: Server Error',
  ONELINK_TIMEOUT: 'No Http Response Exception',
  ACCOUNT_DORMANT: 'Account Dormant'
};
const STATE_FALLBACK_REASONS: Record<string, string> = {
  DOWNLOAD: 'General Exception',
  SCREENING: 'Screening Timeout',
  TITLE_FETCH: 'Duplicate Transaction',
  BALANCE_CHECK: 'Balance Inquiry Failed With Code: 500, Desc: Server Error',
  FT_POST: 'General Exception',
  RAAST_POST: 'No Http Response Exception',
  ONELINK_POST: 'No Http Response Exception',
  RTGS_POST: 'Beneficiary Bank Rejected'
};
const formatDateTime = (value?: string) => {
  if (!value) return '-';
  return new Date(value).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};
const getFailureCode = (row: any) => {
  const history = Array.isArray(row.stateHistory) ? row.stateHistory : [];
  const failedEntry = [...history].reverse().find((entry: any) => entry.failReasonCode || entry.reason || entry.comment);
  return failedEntry?.failReasonCode || row.failReasonCode || '';
};
const getFailureReason = (row: any) => {
  const code = getFailureCode(row);
  if (code && REASON_LABELS[code]) return REASON_LABELS[code];
  if (code) return code.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
  if (row.manualAction?.comment) return row.manualAction.comment;
  return STATE_FALLBACK_REASONS[row.currentState] || 'General Exception';
};
const hasPendingOrApprovedSettlement = (row: any) => {
  const settlement = transactionStore.getManualSettlementForTransaction(row.id);
  if (settlement && ['PENDING_CHECKER', 'APPROVED'].includes(settlement.status)) return true;
  const override = transactionStore.getTransactionOverride(row.id);
  return override?.manualSettlementStatus === 'PENDING_CHECKER' || override?.manualSettlementStatus === 'APPROVED';
};
const canMoveToRTGS = (row: any) => row.canMoveToRTGS || ['TITLE_FETCH', 'BALANCE_CHECK', 'FT_POST'].includes(row.currentState) && row.payoutType !== 'OTC';
const canCancel = (row: any) => ['FAILED', 'IN_PROGRESS'].includes(row.lifecycleStatus) && !hasPendingOrApprovedSettlement(row);
const canRetry = (row: any) => ['FAILED', 'IN_PROGRESS'].includes(row.lifecycleStatus) && !['RTGS_POST'].includes(row.currentState) && !hasPendingOrApprovedSettlement(row);
const canManuallySettle = (row: any) => getFailureCode(row) === 'DUPLICATE_TXN' && !hasPendingOrApprovedSettlement(row);
// COC (OTC) eligible to cancel: only OTC transactions, not on Fund Transfer (FT_POST) or Paid (SUCCESS) status,
// still in a cancellable failed/in-progress state, with no cancellation request already in flight.
const canCocCancel = (row: any) => row.payoutType === 'OTC' && row.currentState !== 'FT_POST' && row.lifecycleStatus !== 'SUCCESS' && ['FAILED', 'IN_PROGRESS'].includes(row.lifecycleStatus) && !hasPendingOrApprovedSettlement(row) && transactionStore.getCocCancellationStatusForTransaction(row.id) === 'NONE';
export function FailedTabsContent() {
  const [activeTab, setActiveTab] = useState<FailedActionTabId>('all');
  const [allTransactions, setAllTransactions] = useState<any[]>([]);
  const [storeUpdateCount, setStoreUpdateCount] = useState(0);
  const [reasonSearch, setReasonSearch] = useState('');
  const [codeSearch, setCodeSearch] = useState('');
  const [selectedTransaction, setSelectedTransaction] = useState<any | null>(null);
  const [detailsDrawerOpen, setDetailsDrawerOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cocCancelDialogOpen, setCocCancelDialogOpen] = useState(false);
  const [rtgsDialogOpen, setRtgsDialogOpen] = useState(false);
  const [manualSettleDialogOpen, setManualSettleDialogOpen] = useState(false);
  const [manualSettleReason, setManualSettleReason] = useState('');
  const [manualSettleLoading, setManualSettleLoading] = useState(false);
  const [manualSettleError, setManualSettleError] = useState<string | null>(null);
  const {
    showToast
  } = useToast();
  useEffect(() => {
    const unsubscribe = transactionStore.subscribe(() => setStoreUpdateCount(prev => prev + 1));
    return unsubscribe;
  }, []);
  useEffect(() => {
    const demoFailed = DEMO_TRANSACTIONS.filter((t: any) => t.lifecycleStatus === 'FAILED' || t.lifecycleStatus === 'IN_PROGRESS');
    const combined = [...demoFailed, ...MOCK_FAILED_TRANSACTIONS];
    const seen = new Set<string>();
    const unique = combined.filter((txn: any) => {
      if (seen.has(txn.id)) return false;
      seen.add(txn.id);
      return true;
    });
    const updated = unique.map((txn: any) => {
      const override = transactionStore.getTransactionOverride(txn.id);
      if (override) {
        return {
          ...txn,
          amendStatus: override.amendStatus,
          ...(override.lifecycleStatus && {
            lifecycleStatus: override.lifecycleStatus
          })
        };
      }
      const amendStatus = transactionStore.getAmendmentStatusForTransaction(txn.id);
      return amendStatus !== 'NONE' ? {
        ...txn,
        amendStatus
      } : txn;
    }).filter((txn: any) => {
      if (transactionStore.getRTGSOverride(txn.id)) return false;
      const override = transactionStore.getTransactionOverride(txn.id);
      if (override?.amendStatus === 'APPROVED' && override?.lifecycleStatus === 'IN_PROGRESS') return false;
      if (hasPendingOrApprovedSettlement(txn)) return false;
      // Once a COC cancellation request is approved by the checker, the transaction is cancelled and drops off this list
      if (transactionStore.getCocCancellationStatusForTransaction(txn.id) === 'APPROVED') return false;
      const repushed = transactionStore.getRepushedTransactions();
      return !repushed.some((repushedTxn: any) => repushedTxn.id === txn.id);
    });
    setAllTransactions(updated);
  }, [storeUpdateCount]);
  const countForTab = (tabId: FailedActionTabId) => allTransactions.filter(row => {
    if (tabId === 'all') return true;
    if (tabId === 'rtgs') return canMoveToRTGS(row);
    if (tabId === 'cancel') return canCancel(row);
    if (tabId === 'coc-cancel') return canCocCancel(row);
    if (tabId === 'retry') return canRetry(row);
    return canManuallySettle(row);
  }).length;
  const filteredTransactions = useMemo(() => allTransactions.filter(row => {
    if (activeTab === 'rtgs' && !canMoveToRTGS(row)) return false;
    if (activeTab === 'cancel' && !canCancel(row)) return false;
    if (activeTab === 'coc-cancel' && !canCocCancel(row)) return false;
    if (activeTab === 'retry' && !canRetry(row)) return false;
    if (activeTab === 'manual-settlement' && !canManuallySettle(row)) return false;
    const reason = getFailureReason(row).toLowerCase();
    const code = `${row.id} ${row.agentCode || ''} ${row.mtoRef || ''} ${row.bankRef || ''}`.toLowerCase();
    if (reasonSearch && !reason.includes(reasonSearch.toLowerCase())) return false;
    if (codeSearch && !code.includes(codeSearch.toLowerCase())) return false;
    return true;
  }), [activeTab, allTransactions, reasonSearch, codeSearch]);
  const handleRetry = (txn: any) => {
    transactionStore.repushTransaction(txn);
    showToast(`Transaction ${txn.id} retried and will resume from ${txn.currentState.replace(/_/g, ' ')}`, 'success');
  };
  const handleCancelConfirm = async () => {
    if (!selectedTransaction) return;
    showToast(`Cancellation request submitted for ${selectedTransaction.id}`, 'success');
    setCancelDialogOpen(false);
    setSelectedTransaction(null);
  };
  const handleCocCancelConfirm = async (reason: string) => {
    if (!selectedTransaction) return;
    transactionStore.submitCocCancellation(selectedTransaction, reason);
    showToast(`COC cancellation request submitted for ${selectedTransaction.id} and sent to checker`, 'success');
    setCocCancelDialogOpen(false);
    setSelectedTransaction(null);
  };
  const handleRTGSConfirm = (notes?: string) => {
    if (!selectedTransaction) return;
    transactionStore.addToRTGS(selectedTransaction, notes);
    showToast(`Transaction ${selectedTransaction.id} moved to RTGS`, 'success');
    setRtgsDialogOpen(false);
    setSelectedTransaction(null);
  };
  const handleManualSettleConfirm = async () => {
    if (!selectedTransaction) return;
    if (!manualSettleReason.trim()) {
      setManualSettleError('Reason is required to submit for manual settlement');
      return;
    }
    setManualSettleLoading(true);
    setManualSettleError(null);
    await new Promise(resolve => setTimeout(resolve, 700));
    transactionStore.submitManualSettlement(selectedTransaction, manualSettleReason);
    showToast(`Manual settlement request submitted for ${selectedTransaction.id}`, 'success');
    setManualSettleLoading(false);
    setManualSettleDialogOpen(false);
    setManualSettleReason('');
    setSelectedTransaction(null);
  };
  const rowActions: RowAction<any>[] = [{
    icon: 'lucide:eye',
    label: 'View Details',
    onClick: row => {
      setSelectedTransaction(row);
      setDetailsDrawerOpen(true);
    }
  }, {
    icon: 'lucide:arrow-right-circle',
    label: 'Move to RTGS',
    onClick: row => {
      setSelectedTransaction(row);
      setRtgsDialogOpen(true);
    },
    condition: canMoveToRTGS
  }, {
    icon: 'lucide:x-circle',
    label: 'Cancel',
    onClick: row => {
      setSelectedTransaction(row);
      setCancelDialogOpen(true);
    },
    condition: row => activeTab !== 'coc-cancel' && canCancel(row)
  }, {
    icon: 'lucide:landmark',
    label: 'Request COC Cancellation',
    onClick: row => {
      setSelectedTransaction(row);
      setCocCancelDialogOpen(true);
    },
    condition: row => activeTab === 'coc-cancel' && canCocCancel(row)
  }, {
    icon: 'lucide:rotate-cw',
    label: 'Retry',
    onClick: row => handleRetry(row),
    condition: canRetry
  }, {
    icon: 'lucide:stamp',
    label: 'Manually Settle',
    onClick: row => {
      setSelectedTransaction(row);
      setManualSettleDialogOpen(true);
    },
    condition: canManuallySettle
  }];
  const columns: Column<any>[] = [{
    id: 'id',
    label: 'Txn ID',
    renderCell: row => <Typography variant="body2" fontWeight={600} fontFamily="monospace" noWrap data-id="element-1118" data-mp="2n731dj7f5vcy">{row.id}</Typography>
  }, {
    id: 'mtoName',
    label: 'MTO',
    renderCell: row => <Typography variant="body2" fontWeight={500} noWrap sx={{
      minWidth: 140
    }} data-id="element-1119" data-mp="2dm2072fdqw0o">{row.mtoName || 'No MTO'}</Typography>
  }, {
    id: 'payoutType',
    label: 'Type',
    renderCell: row => <Chip label={row.payoutType} size="small" sx={{
      backgroundColor: row.payoutType === 'FT' ? 'rgba(14, 165, 233, 0.1)' : row.payoutType === 'IBFT' ? 'rgba(139, 92, 246, 0.1)' : 'rgba(245, 158, 11, 0.1)',
      color: row.payoutType === 'FT' ? '#0EA5E9' : row.payoutType === 'IBFT' ? '#8B5CF6' : '#F59E0B',
      fontWeight: 600,
      fontSize: '0.7rem'
    }} data-id="element-1120" data-mp="24klydj2ms282" />
  }, {
    id: 'amountFCY',
    label: 'Amount',
    renderCell: row => <Typography variant="body2" fontWeight={600} noWrap sx={{
      minWidth: 100
    }} data-id="element-1121" data-mp="2nhn8fib1ksiv">{row.currencyFCY} {row.amountFCY}</Typography>
  }, {
    id: 'lifecycleStatus',
    label: 'Lifecycle',
    renderCell: row => <Chip label={row.lifecycleStatus?.replace(/_/g, ' ')} size="small" sx={{
      backgroundColor: row.lifecycleStatus === 'FAILED' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(59, 130, 246, 0.1)',
      color: row.lifecycleStatus === 'FAILED' ? '#EF4444' : '#3B82F6',
      fontWeight: 600,
      fontSize: '0.7rem'
    }} data-id="element-1122" data-mp="3f8jcpuj1douj" />
  }, {
    id: 'reason',
    label: 'Reason',
    renderCell: row => <Typography variant="body2" sx={{
      minWidth: 260,
      maxWidth: 320,
      whiteSpace: 'normal',
      lineHeight: 1.35
    }} data-id="element-1123" data-mp="1iavpwd4ypppa">{getFailureReason(row)}</Typography>,
    renderFilter: () => <TextField size="small" placeholder="Search by Reason" value={reasonSearch} onChange={e => setReasonSearch(e.target.value)} sx={{
      width: '100%',
      minWidth: 240
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-1125" data-mp="koob6aey2gz7"><Icon icon="lucide:search" width={14} height={14} data-id="element-1126" data-mp="kphmzpsrfwo7" /></InputAdornment>,
      sx: {
        height: 36,
        fontSize: '0.8rem'
      }
    }} data-id="element-1124" data-mp="1dcanq865gvy7" />
  }, {
    id: 'agentCode',
    label: 'Agent Code',
    renderCell: row => <Typography variant="body2" noWrap sx={{
      minWidth: 120
    }} data-id="element-1127" data-mp="undve9ubcdim">{row.agentCode || '-'}</Typography>,
    renderFilter: () => <TextField size="small" placeholder="Search by Code" value={codeSearch} onChange={e => setCodeSearch(e.target.value)} sx={{
      width: '100%',
      minWidth: 160
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-1129" data-mp="flffvolf79pt"><Icon icon="lucide:search" width={14} height={14} data-id="element-1130" data-mp="ki5r9yma5h4w" /></InputAdornment>,
      sx: {
        height: 36,
        fontSize: '0.8rem'
      }
    }} data-id="element-1128" data-mp="2b1dujilc3z4t" />
  }, {
    id: 'createdAt',
    label: 'Created At',
    renderCell: row => <Typography variant="body2" fontSize="0.8rem" noWrap sx={{
      minWidth: 170
    }} data-id="element-1131" data-mp="tj3zyu7nyctt">{formatDateTime(row.createdAt)}</Typography>
  }, {
    id: 'updatedAt',
    label: 'Last Update',
    renderCell: row => <Typography variant="body2" fontSize="0.8rem" noWrap sx={{
      minWidth: 170
    }} data-id="element-1132" data-mp="ygdxyy94bwt9">{formatDateTime(row.updatedAt || row.failedAt)}</Typography>
  }];
  return <Box data-id="element-1133" data-mp="189b8oi7vchhb">
      <Box sx={{
      borderBottom: 1,
      borderColor: 'divider',
      mb: 2
    }} data-id="element-1134" data-mp="188jp65rjqa7e">
        <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value)} variant="scrollable" scrollButtons="auto" sx={{
        minHeight: 48,
        '& .MuiTab-root': {
          textTransform: 'uppercase',
          fontWeight: 600,
          minHeight: 48,
          color: '#475569'
        }
      }} data-id="element-1135" data-mp="1c9v49hcsnd1c">
          {ACTION_TABS.map(tab => {
          const count = countForTab(tab.id);
          return <Tab key={tab.id} value={tab.id} label={<Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }} data-id="element-1137" data-mp="1rcwkqb9ftfu4"><Icon icon={tab.icon} width={17} height={17} data-id="element-1138" data-mp="1rbbpskj7nuvq" /><Typography variant="body2" fontWeight={700} data-id="element-1139" data-mp="1mdab0bstihq2">{tab.label}</Typography><Chip label={count} size="small" sx={{
              height: 22,
              fontSize: '0.7rem',
              fontWeight: 700,
              backgroundColor: count > 0 ? 'rgba(239, 68, 68, 0.1)' : 'rgba(156, 163, 175, 0.12)',
              color: count > 0 ? '#EF4444' : '#9CA3AF'
            }} data-id="element-1140" data-mp="1mfmp2dvgg7xu" /></Box>} data-id="element-1136" data-mp="1wd13y91qcoo1" />;
        })}
        </Tabs>
      </Box>

      <DataTable title="Failed Transactions" data={filteredTransactions} columns={columns} rowActions={rowActions} keyField="id" emptyStateMessage="No failed transactions match this action queue" initialVisibleColumns={['id', 'mtoName', 'payoutType', 'amountFCY', 'lifecycleStatus', 'reason', 'agentCode', 'createdAt', 'updatedAt']} onRowClick={row => {
      setSelectedTransaction(row);
      setDetailsDrawerOpen(true);
    }} data-id="element-1141" data-mp="2a3nzjsc71yfw" />

      {selectedTransaction && <TransactionDetailsDrawer open={detailsDrawerOpen} onClose={() => {
      setDetailsDrawerOpen(false);
      setSelectedTransaction(null);
    }} transaction={{
      ...selectedTransaction,
      currentState: selectedTransaction.currentState as any,
      lifecycleStatus: selectedTransaction.lifecycleStatus as any
    } as any} data-id="element-1142" data-mp="r4q6bglqdgu9" />}
      {selectedTransaction && <CancelTransactionDialog open={cancelDialogOpen} onClose={() => {
      setCancelDialogOpen(false);
      setSelectedTransaction(null);
    }} onConfirm={handleCancelConfirm} transactionId={selectedTransaction.id} cancellationAction={selectedTransaction.cancellationAction || 'SIMPLE_CANCEL'} data-id="element-1143" data-mp="mcol356l4fs3" />}
      {selectedTransaction && <CancelTransactionDialog open={cocCancelDialogOpen} onClose={() => {
      setCocCancelDialogOpen(false);
      setSelectedTransaction(null);
    }} onConfirm={handleCocCancelConfirm} transactionId={selectedTransaction.id} cancellationAction="COC_CANCELLATION_REQUEST" data-id="element-1144" data-mp="3umgpvclw3n4a" />}
      {selectedTransaction && <RTGSActionDialog open={rtgsDialogOpen} onClose={() => {
      setRtgsDialogOpen(false);
      setSelectedTransaction(null);
    }} onConfirm={handleRTGSConfirm} transaction={selectedTransaction} action="send" data-id="element-1145" data-mp="1xt5232xsf4h2" />}

      <Dialog open={manualSettleDialogOpen} onClose={() => {
      if (!manualSettleLoading) {
        setManualSettleDialogOpen(false);
        setManualSettleReason('');
        setManualSettleError(null);
        setSelectedTransaction(null);
      }
    }} maxWidth="sm" fullWidth data-id="element-1146" data-mp="3dcw190o91st1">
        <DialogTitle data-id="element-1147" data-mp="3n7xofqyysksz"><Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }} data-id="element-1148" data-mp="3n87flhvqwy09"><Icon icon="lucide:stamp" width={24} color="#0EA5E9" data-id="element-1149" data-mp="4uq1m5cfc0xc" /><Typography variant="h6" fontWeight={600} data-id="element-1150" data-mp="upqatkmg5ui">Manually Settle Transaction</Typography></Box></DialogTitle>
        <DialogContent data-id="element-1151" data-mp="euqryb8pnhb1"><Box sx={{
          mt: 1
        }} data-id="element-1152" data-mp="euh0skbxj43r">{selectedTransaction && <Typography variant="body2" color="text.secondary" sx={{
            mb: 2
          }} data-id="element-1153" data-mp="es4k0ahwqzsa">Transaction ID: <strong data-id="element-1154" data-mp="9vnlqngzv51h">{selectedTransaction.id}</strong></Typography>}<Alert severity="info" sx={{
            mb: 3
          }} data-id="element-1155" data-mp="2glc0vod65pga">This transaction failed with duplicate transaction evidence. Submit it for manual settlement after confirming the beneficiary credit.</Alert><TextField label="Settlement Reason" placeholder="Enter confirmation details for manual settlement..." multiline rows={4} fullWidth required value={manualSettleReason} onChange={e => {
            setManualSettleReason(e.target.value);
            setManualSettleError(null);
          }} error={!!manualSettleError} helperText={manualSettleError} disabled={manualSettleLoading} data-id="element-1156" data-mp="2botwwqpnxv50" /></Box></DialogContent>
        <DialogActions sx={{
        px: 3,
        pb: 2
      }} data-id="element-1157" data-mp="24hg0e6fb89tv"><Button onClick={() => {
          setManualSettleDialogOpen(false);
          setManualSettleReason('');
          setManualSettleError(null);
          setSelectedTransaction(null);
        }} disabled={manualSettleLoading} data-id="element-1158" data-mp="16upgzoonk97p">Cancel</Button><Button variant="contained" onClick={handleManualSettleConfirm} disabled={manualSettleLoading || !manualSettleReason.trim()} sx={{
          bgcolor: '#0EA5E9',
          '&:hover': {
            bgcolor: '#0284C7'
          }
        }} startIcon={manualSettleLoading ? <CircularProgress size={16} color="inherit" data-id="element-1160" data-mp="2s8bsz5rlb926" /> : <Icon icon="lucide:send" width={16} data-id="element-1161" data-mp="2s7itmpuynlu2" />} data-id="element-1159" data-mp="1gsn3o7qv5xy7">Submit for Settlement</Button></DialogActions>
      </Dialog>
    </Box>;
}
export default FailedTabsContent;